import Box from '@mui/material/Box';
import { useFormikContext } from 'formik';
import {
  type FC,
  type ReactElement,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
} from 'react';

import { SlabContext } from '@core/context/SlabContext';
import type { ProjectFormikType, RequiredProjectFieldsInfo } from '@core/formik/project';
import type { Company } from '@core/types/api/Company/Company';
import type { DeliveryCost } from '@core/types/api/DeliveryCost/DeliveryCost';
import Enums from '@core/types/api/Enums';
import type { List } from '@core/types/list';
import { NullableCostString } from '@core/utils/currency';
import { hasFormikFieldsErrors } from '@core/utils/formikHelpers';
import { requiredLabel } from '@core/utils/strings';
import { isDefaultUUID } from '@core/utils/uuid';
import type { FileUploadsManager } from '@web/components/Files';
import { Input } from '@web/components/Input/Input';
import type { InputOption } from '@web/components/InputDropdown/InputDropdown';
import type { ProductSectionProps } from '@web/components/ProductSection/ProductSection';
import { SlabStepper } from '@web/components/SlabStepper/SlabStepper';
import { useStepper } from '@web/components/SlabStepper/useStepper';
import { lookups } from '@web/utils/DomainHelpers';

import { ProjectSection } from '../types';
import { useForecastSync } from '../useForecastSync';
import { usePlantAutoselect } from '../usePlantAutoselect';
import { usePlantDistanceCalculationForProjects } from '../usePlantDistanceCalculationForProjects';
import { CustomFieldsSection } from './CustomFieldsSection';
import { DetailsSection } from './DetailsSection';
import { DynamicCompanyLookup } from './DynamicCompanyLookup';
import { ForecastsSection } from './ForecastsSection';
import { LocationsSummarySection } from './LocationsSummarySection';
import ProductsByPlantSection from './ProductsByPlantSection';
import { ProjectAddressSection } from './ProjectAddressSection';
import { ProjectConfigSection } from './ProjectConfigSection';
import { ProjectFilesSection } from './ProjectFilesSection';
import { ProjectInformationSection } from './ProjectInformationSection';

export { ProjectSection } from '../types';

export const RecalculateTotalDeliveryCost = (deliveryCosts: DeliveryCost | null): string =>
  NullableCostString({ cost: deliveryCosts?.calculateTotalDeliveryCost() ?? null });

/**
 * Generate lookup options for potential project-winning customers.
 */
export const WinnerOptions = ({
  currentContractors,
  companyList,
}: {
  currentContractors: ProjectFormikType['contractors'];
  companyList: List<Company>;
}): InputOption[] => {
  const currentCompanyIDs = currentContractors
    .map((c) => c.company.id)
    .filter((id) => id !== null) as string[];
  const eligibleCompanies = companyList.items.filter((c) => currentCompanyIDs.includes(c.id));

  const result = lookups({
    items: eligibleCompanies,
    label: (c) => c.name,
    count: eligibleCompanies.length,
  });

  return result;
};

interface TextareaSectionProps {
  fieldName: keyof ProjectFormikType & string;
}

const TextareaSection: FC<TextareaSectionProps> = ({ fieldName }) => (
  <Box>
    <Input type='textarea' name={fieldName} />
  </Box>
);

type ProjectDrawerSectionsProps = {
  initialSection: ProjectSection;
  initialForecastYear: number;
  curProductsRef: ProductSectionProps['curProductsRef'];
  omitCustomFieldSection: boolean;
  focusField?: string;
  requiredFields: RequiredProjectFieldsInfo;
  /** Drawer-level buffered upload queue, owned by ProjectDrawer so it survives step
   * navigation (MUI StepContent unmounts inactive steps) and outlives the Files step
   * itself -- ProjectDrawer's create-save success handler attaches the queued files. */
  filesManager: FileUploadsManager;
};

export const ProjectDrawerSections = ({
  initialSection,
  initialForecastYear,
  curProductsRef,
  omitCustomFieldSection,
  focusField,
  requiredFields,
  filesManager,
}: ProjectDrawerSectionsProps): ReactElement => {
  const formikBag = useFormikContext<ProjectFormikType>();
  const ctx = useContext(SlabContext);
  const filesFlagEnabled = ctx.userInfo.hasFlags([
    Enums.FeatureFlagName.FeatureFlagFileAttachments,
  ]);

  usePlantDistanceCalculationForProjects();
  usePlantAutoselect();
  useForecastSync();

  const { activeStep, changeStep } = useStepper({
    initialStep: initialSection,
    steps: [],
  });

  const changeStepRef = useRef(changeStep);
  useEffect(() => {
    changeStepRef.current = changeStep;
  }, [changeStep]);

  const handleNavigateSection = useCallback((section: ProjectSection) => {
    changeStepRef.current(section);
  }, []);

  const ProjectConfig = useCallback(
    () => <ProjectConfigSection curProductsRef={curProductsRef} />,
    [curProductsRef],
  );
  const Companies = useCallback(() => <DynamicCompanyLookup />, []);

  const PlantProducts = useCallback(
    () => <ProductsByPlantSection curProductsRef={curProductsRef} />,
    [curProductsRef],
  );
  const LocationsSummary = useCallback(() => <LocationsSummarySection />, []);
  const Forecasts = useCallback(
    () => (
      <ForecastsSection
        initialForecastYear={initialForecastYear}
        onNavigateSection={handleNavigateSection}
      />
    ),
    [initialForecastYear, handleNavigateSection],
  );
  const Terms = useCallback(() => <TextareaSection fieldName='terms' />, []);
  const Notes = useCallback(() => <TextareaSection fieldName='notes' />, []);
  const Details = useCallback(
    () => <DetailsSection requiredFields={requiredFields} />,
    [requiredFields],
  );
  const ProjectInformationSectionWithFocus = useCallback(
    () => <ProjectInformationSection focusField={focusField} requiredFields={requiredFields} />,
    [focusField],
  );
  const projectId = formikBag.values.id;
  const isNewProject = isDefaultUUID(projectId);
  const Files = useCallback(
    () => (
      <ProjectFilesSection projectID={isNewProject ? null : projectId} manager={filesManager} />
    ),
    [isNewProject, projectId, filesManager],
  );

  const steps = useMemo(
    () =>
      [
        {
          label: ProjectSection.PROJECT_INFORMATION,
          content: ProjectInformationSectionWithFocus,
          error:
            formikBag.submitCount > 0 &&
            hasFormikFieldsErrors(formikBag, [
              'name',
              'projectStatus',
              'user',
              'estimatedStartDate',
              'estimatedEndDate',
              'expirationDate',
              'winningCompany',
              'lossReason',
            ]) === true,
        },
        {
          hidden: omitCustomFieldSection,
          label: ProjectSection.ADDITIONAL_INFORMATION,
          content: CustomFieldsSection,
          error:
            formikBag.submitCount > 0 &&
            hasFormikFieldsErrors(formikBag, ['customFields']) === true,
        },
        {
          label: ProjectSection.JOB_SITE_ADDRESS,
          content: ProjectAddressSection,
          error:
            formikBag.submitCount > 0 && hasFormikFieldsErrors(formikBag, ['address']) === true,
        },
        {
          label: requiredLabel(ProjectSection.PROJECT_CONFIG, requiredFields.config),
          content: ProjectConfig,
          error:
            formikBag.submitCount > 0 &&
            hasFormikFieldsErrors(formikBag, ['projectConfig']) === true,
        },
        {
          label: ProjectSection.COMPANIES,
          content: Companies,
          error:
            formikBag.submitCount > 0 &&
            hasFormikFieldsErrors(formikBag, ['contractors', 'otherCompanies']) === true,
        },
        {
          label: requiredLabel(ProjectSection.PRODUCT_INFO, requiredFields.products),
          content: PlantProducts,
          error:
            formikBag.submitCount > 0 &&
            hasFormikFieldsErrors(formikBag, ['productsByPlants']) === true,
        },
        {
          label: ProjectSection.LOCATIONS_INFO,
          content: LocationsSummary,
        },
        {
          label: ProjectSection.DETAILS,
          content: Details,
          error:
            formikBag.submitCount > 0 &&
            hasFormikFieldsErrors(formikBag, ['revenueOverride', 'readyMixQuantityOverride']) ===
              true,
        },
        {
          label: ProjectSection.FORECASTS,
          content: Forecasts,
        },
        {
          label: ProjectSection.TERMS,
          content: Terms,
        },
        {
          label: ProjectSection.NOTES,
          content: Notes,
        },
        {
          hidden: !filesFlagEnabled,
          label: ProjectSection.FILES,
          content: Files,
        },
      ].filter((step) => step.hidden !== true),
    [
      formikBag,
      omitCustomFieldSection,
      ProjectConfig,
      Companies,
      PlantProducts,
      Forecasts,
      Terms,
      Notes,
      Files,
      filesFlagEnabled,
      ProjectInformationSectionWithFocus,
      CustomFieldsSection,
      ProjectAddressSection,
      Details,
      LocationsSummarySection,
      requiredFields,
    ],
  );

  return <SlabStepper activeStep={activeStep} onChangeStep={changeStep} steps={steps} />;
};
